import cn from 'classnames';
import { find } from 'lodash';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from 'antd';

import ArrowUpIcon from '@/comp/common/icons/ArrowUpIcon';
import PlusIcon from '@/comp/common/icons/PlusIcon';
import DeleteItemPopconfirmIcon from '@/comp/pages/EnginePage/Engine/Common/DeleteItemPopconfirmIcon/DeleteItemPopconfirmIcon';
import ValidationTooltip from '@/comp/pages/EnginePage/Engine/Common/ValidationTooltip/ValidationTooltip';
import { useStores } from '@/hooks/use-stores';
import { EngineStepsEnum, SensorsLogicEnum, SensorsAvailabilityEnum } from '@/store/Engine/constants';
import { EPButton, EPSelect, EPSelectOption } from '@/components';

import { SensorsPriorityContainer } from './styled';

const { useToken } = theme;

const SensorsPriority = observer(({ type, hasTriedToSubmit }: { type: string; hasTriedToSubmit: boolean }) => {
  const { t } = useTranslation();
  const { engineStore, devicesStore } = useStores();
  const sensorsPriority = engineStore.sensorsConfiguration?.[`${type}SensorsPriority`];
  const { token } = useToken();

  const sensorsOptions = useMemo(
    () => devicesStore[`${type}Sensors`]?.map(device => ({ label: device?.name, value: device?.id })),
    [devicesStore[`${type}Sensors`]],
  );

  const onDeleteIconClick = sensorPriorityKey => () => sensorsPriority?.deleteSensorPriority(sensorPriorityKey);

  const onArrowUpClick = index => () => sensorsPriority?.moveSensorPriority(index, index - 1);

  const onArrowDownClick = index => () => sensorsPriority?.moveSensorPriority(index, index + 1);

  const onAddFallbackClick = () => sensorsPriority?.addSensorPriority(type);

  const onSensorPriorityFieldChange = (sensorPriorityKey, field) => value => {
    sensorsPriority?.setSensorPriorityField(
      sensorPriorityKey,
      field,
      field === 'sensors'
        ? value?.map(itemValue => ({
            id: itemValue,
            name: devicesStore.deviceMetadataFromDeviceId(itemValue)?.name,
          }))
        : value,
    );
    if (hasTriedToSubmit)
      engineStore.validate(
        EngineStepsEnum.SensorsConfiguration,
        `${type}SensorsPriority`,
        null,
        `${field}-${sensorPriorityKey}`,
      );
  };

  const showAddFallbackButton = !engineStore.isReadOnly && !(engineStore.isNoFlowControl && type === 'flow');

  return (
    <SensorsPriorityContainer $hasFinishedWizard={engineStore.hasFinishedWizard} $isReadOnly={engineStore.isReadOnly}>
      {sensorsPriority?.sensorsPriority?.map((sensorPriority, index) => (
        <div className="sensor-priority" key={sensorPriority?.priority}>
          <div className="sensor-priority-header">
            <span>{sensorPriority?.getPriorityName(t)}</span>
            <div className="priority-icons">
              {sensorsPriority?.sensorsPriority?.length > 1 && index !== 0 && (
                <DeleteItemPopconfirmIcon
                  itemName={sensorPriority?.getPriorityName(t)}
                  onConfirm={onDeleteIconClick(sensorPriority?.key)}
                  className={cn(
                    (index === 0 || index === sensorsPriority?.sensorsPriority?.length - 1) && 'margin-right',
                  )}
                />
              )}
              {sensorsPriority?.sensorsPriority?.length > 1 && index !== 0 ? (
                <div className="arrows">
                  {index > 0 && <ArrowUpIcon className="arrow" onClick={onArrowUpClick(index)} />}
                  {index < sensorsPriority?.sensorsPriority?.length - 1 && (
                    <ArrowUpIcon className={cn('arrow', 'transform-down')} onClick={onArrowDownClick(index)} />
                  )}
                </div>
              ) : null}
            </div>
          </div>
          <div className="header">
            <span className="logic">{t('logic')}</span>
            <span className="online">{t('online')}</span>
            <span>{t('sensors')}</span>
          </div>
          <div className="values">
            <ValidationTooltip
              message={
                hasTriedToSubmit &&
                find(engineStore.validationMessages, {
                  key: sensorPriority?.key,
                  field: 'sensors',
                  focused: true,
                })?.message
              }
            >
              <EPSelect
                value={sensorPriority?.aggregation}
                onChange={onSensorPriorityFieldChange(sensorPriority?.key, 'aggregation')}
                size="small"
                className="select"  
                disabled={index === 0}
              >
                {Object.values(SensorsLogicEnum).map(aggregation =>
                  aggregation === SensorsLogicEnum.Sum && type === 'pressure' ? null : (
                    <EPSelectOption value={aggregation} key={aggregation}>
                      {t(aggregation)}
                    </EPSelectOption>
                  ),
                )}
              </EPSelect>
            </ValidationTooltip>
            <EPSelect
              value={sensorPriority?.availability}
              onChange={onSensorPriorityFieldChange(sensorPriority?.key, 'availability')}
              size="small"
              className="select"
              disabled={index === 0}
            >
              <EPSelectOption value={SensorsAvailabilityEnum.Any} key="any">
                {t('any')}
              </EPSelectOption>
              <EPSelectOption value={SensorsAvailabilityEnum.All} key="all">
                {t('all')}
              </EPSelectOption>
            </EPSelect>
            <EPSelect
              mode="multiple"
              size="small"
              className="sensors-multiselect"
              value={sensorPriority?.sensors?.map(sensor => ({
                value: sensor?.id,
                label:
                  devicesStore.deviceMetadataFromDeviceId(sensor?.id)?.name ??
                  engineStore.pidChanges?.[`${type}SensorsRemoved`].find(
                    removedSensor => removedSensor?.deviceId === sensor?.id,
                  )?.deviceName,
              }))}
              options={sensorsOptions}
              onChange={onSensorPriorityFieldChange(sensorPriority?.key, 'sensors')}
              suffixIcon={<PlusIcon color={token.colorBorder} />}
              filterOption={(input, option) =>
                option?.label && option.label?.toLowerCase()?.includes(input?.toLocaleLowerCase())
              }
              disabled={index === 0}
            />
          </div>
        </div>
      ))}
      {showAddFallbackButton && (
        <EPButton size="medium" type="primary" onClick={onAddFallbackClick} className="add-fallback-button">
          <span className="button-label">{t('addFallback')}</span>
        </EPButton>
      )}
    </SensorsPriorityContainer>
  );
});

export default SensorsPriority;
